/**
 * One piece of asynchronous work whose result every caller of
 * {@link SharedTask.wait} shares.
 *
 * The work runs under an abort signal that the task creates. A caller whose
 * signal has aborted, before it calls `wait` or while it waits, is rejected
 * with its signal's reason while the other callers keep waiting. When that
 * abort leaves no caller waiting before the work settles, the task aborts
 * the work's signal.
 */
export class SharedTask<T> {
  private readonly controller = new AbortController();
  private settled = false;
  private waiting = 0;
  private readonly work: Promise<T>;

  /** Start `run` at once, under the task's own signal. */
  constructor(run: (signal: AbortSignal) => Promise<T>) {
    const signal = this.controller.signal;
    // A synchronous throw from `run` becomes the work's failure.
    this.work = new Promise<T>((resolve) => resolve(run(signal)));
    const settle = () => {
      this.settled = true;
    };
    // The rejection handler also keeps a failure that no caller waits on
    // from being reported as an unhandled rejection.
    void this.work.then(settle, settle);
  }

  /**
   * The work's value, or its failure. If `signal` aborts first, this call
   * rejects with the signal's reason; a `signal` already aborted rejects at
   * once. A call without a `signal` waits until the work settles, so the
   * work is never cancelled while it waits.
   */
  wait(signal?: AbortSignal): Promise<T> {
    if (signal === undefined) {
      this.waiting += 1;
      return this.work;
    }
    if (signal.aborted) {
      this.cancelIfUnwaited();
      return Promise.reject(signal.reason as Error);
    }
    this.waiting += 1;
    return new Promise<T>((resolve, reject) => {
      const leave = () => {
        this.waiting -= 1;
        reject(signal.reason as Error);
        this.cancelIfUnwaited();
      };
      signal.addEventListener("abort", leave, { once: true });
      void this.work
        .then(resolve, reject)
        .finally(() => signal.removeEventListener("abort", leave));
    });
  }

  /** Abort the work when it has not settled and no caller waits on it. */
  private cancelIfUnwaited(): void {
    if (this.settled || this.waiting > 0) {
      return;
    }
    this.controller.abort();
  }
}
